// Import 3rd-party dependencies
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { debounceTime, expand, switchMap, takeWhile, tap } from 'rxjs/operators';
import { SubSink } from 'subsink';
import { of } from 'rxjs';

// Import project-specific dependencies
import { InsightsService } from '@app/core/analytics/services/app-insights-service';
import { RecentSearchService } from '@app/services/recent-search/recent-search.service';
import { EnterpriseSearchService } from '@app/services/enterprise-search/enterprise-search.service';
import { SearchDataService } from '@app/services/searchdata/searchdata.service';
import { SEARCH_COMPONENT_CONSTS } from '@app/config/constants';

@Component({
  selector: 'app-search',
  templateUrl: './search.component.html',
  styleUrls: ['./search.component.css']
})
export class SearchComponent implements OnInit, OnDestroy {
  subs: SubSink;

  // Class attributes
  maxRecentSearchTerms: number;
  searchForm: FormGroup;
  consts = SEARCH_COMPONENT_CONSTS;

  recents$ = this.recentSearchService.searches$;

  constructor(
    private trackingService: InsightsService,
    private recentSearchService: RecentSearchService,
    private enterpriseSearchService: EnterpriseSearchService,
    private searchDataService: SearchDataService
  ) {}

  ngOnInit(): void {
    this.subs = new SubSink();
    this.searchForm = new FormGroup({
      searchInput: new FormControl()
    });
    this.subs.sink = this.searchForm.get('searchInput').valueChanges.pipe(
      debounceTime(this.consts.DEBOUNCE_TIME)
    ).subscribe();
    this.recentSearchService.getRecents();    
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  onSubmitOffice(): void {
    let searchInput = this.searchForm.get('searchInput').value as string;
    searchInput = searchInput == null ? '' : searchInput.trim();

    this.recentSearchService.addRecentSearch(searchInput);
    this.trackingService.trackSearchInput({
      type: this.consts.APP_INSIGHTS_SEARCH_TYPE,
      term: searchInput
    });
    
    const searchContentSources = [
      this.consts.CONNECTION_HR_CONNECT,
      this.consts.CONNECTION_IT_CONNECT
    ];
    
    this.subs.sink = of({
      from: 0,
      moreResultsAvailable: false
    })
      .pipe(
        expand((data) =>
          this.enterpriseSearchService.contentSourceSearch(
            searchInput || this.consts.SEARCH_WILDCARD,
            searchContentSources,
            data.from
          ).pipe(
            tap((result) => {
              data.from = data.from + result.searchData.length;
              data.moreResultsAvailable = result.moreResultsAvailable;              
            }),
            switchMap((result) => {
              delete result.moreResultsAvailable;
              delete result.total;
              const newDataArr = result.searchData.map((searchDataContent) => ({
                id: searchDataContent.resource['sysID'],
                contentSource: searchDataContent.contentSource,
                rank: searchDataContent.rank,
                ...searchDataContent.resource
              }));
              return this.searchDataService.addSearchData(newDataArr);
            }),
            switchMap(() => of(data)),
            takeWhile((data) => data.moreResultsAvailable)
          )
        )
      )
      .subscribe();            
    window.open(`${this.consts.OFFICE_URL}${searchInput}`, '_blank');
  }
}
